import { useState } from 'react';

export interface SymbolPickerProps {
  label?: string;
  value: string;
  onChange: (next: string) => void;
  /** List of symbol strings to show in the picker, e.g. activeOptions.data.map(o => o.symbol) */
  options: string[];
  placeholder?: string;
  isLoading?: boolean;
}

/**
 * Поле ввода символа опциона с всплывающим bottom-sheet пикером.
 * Работает в iOS Telegram WebView (WKWebView), где <datalist> не поддерживается.
 * Пользователь может как набрать символ вручную, так и выбрать из списка активных опционов.
 */
export function SymbolPicker({
  label,
  value,
  onChange,
  options,
  placeholder = 'BTC-20250131-50000-C',
  isLoading = false,
}: SymbolPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const openPicker = (): void => {
    setSearch(value);
    setOpen(true);
  };

  const closePicker = (): void => {
    setOpen(false);
    setSearch('');
  };

  const select = (sym: string): void => {
    onChange(sym);
    closePicker();
  };

  const q = search.trim().toUpperCase();
  const filtered = q
    ? options.filter((o) => o.toUpperCase().includes(q))
    : options;
  const exactMatch = options.some((o) => o.toUpperCase() === q);
  const showUseTyped = q.length > 0 && !exactMatch;

  return (
    <>
      <label
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: 'var(--space-md) var(--space-lg)',
          borderBottom: '0.5px solid var(--tg-separator)',
        }}
      >
        {label && (
          <span style={{ fontSize: 'var(--font-sm)', color: 'var(--tg-hint)' }}>{label}</span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoCapitalize="characters"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              padding: 0,
              fontSize: 'var(--font-md)',
              color: 'var(--tg-text)',
              minHeight: 24,
            }}
          />
          <button
            type="button"
            onClick={openPicker}
            disabled={isLoading}
            aria-label="Выбрать символ из списка"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 4,
              cursor: 'pointer',
              color: 'var(--tg-hint)',
              fontSize: 20,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              minHeight: 'var(--tap-target)',
              minWidth: 'var(--tap-target)',
              justifyContent: 'center',
            }}
          >
            {isLoading ? '…' : '▾'}
          </button>
        </div>
      </label>

      {open && (
        /* Backdrop */
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            background: 'rgba(0,0,0,0.45)',
          }}
          onClick={closePicker}
        >
          {/* Bottom sheet */}
          <div
            style={{
              background: 'var(--tg-secondary-bg)',
              borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              maxHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search bar */}
            <div
              style={{
                padding: 'var(--space-md) var(--space-lg)',
                borderBottom: '0.5px solid var(--tg-separator)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
              }}
            >
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск…"
                autoCapitalize="characters"
                style={{
                  flex: 1,
                  background: 'var(--tg-bg)',
                  border: 'none',
                  outline: 'none',
                  padding: 'var(--space-sm) var(--space-md)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-md)',
                  color: 'var(--tg-text)',
                }}
              />
              <button
                type="button"
                onClick={closePicker}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--tg-hint)',
                  fontSize: 'var(--font-md)',
                  cursor: 'pointer',
                  padding: 'var(--space-xs)',
                  minHeight: 'var(--tap-target)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Закрыть
              </button>
            </div>

            {/* Symbol list */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {showUseTyped && (
                <button
                  type="button"
                  onClick={() => select(q)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: 'var(--space-md) var(--space-lg)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '0.5px solid var(--tg-separator)',
                    color: 'var(--tg-button)',
                    fontSize: 'var(--font-md)',
                    cursor: 'pointer',
                    minHeight: 'var(--tap-target)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Использовать «{q}»
                </button>
              )}
              {filtered.map((sym) => (
                <button
                  key={sym}
                  type="button"
                  onClick={() => select(sym)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: 'var(--space-md) var(--space-lg)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '0.5px solid var(--tg-separator)',
                    color: 'var(--tg-text)',
                    fontSize: 'var(--font-md)',
                    cursor: 'pointer',
                    minHeight: 'var(--tap-target)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {sym}
                </button>
              ))}
              {filtered.length === 0 && !showUseTyped && (
                <div
                  style={{
                    padding: 'var(--space-xl) var(--space-lg)',
                    color: 'var(--tg-hint)',
                    fontSize: 'var(--font-md)',
                    textAlign: 'center',
                  }}
                >
                  Нет совпадений
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
